import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { Task } from "../../types";

interface SubtaskUpdates {
  title?: string;
  completed?: boolean;
  order_position?: number;
  description?: string;
  priority?: number;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  deadline_date?: string | null;
  duration?: number | null;
  persistent?: boolean;
  working_hours_only?: boolean;
}

/**
 * Returns an `updateSubtask(subtaskId, updates)` that optimistically
 * applies field changes to a single subtask in the tasks cache — reflected
 * instantly in every consumer of `useTasks` (e.g. the open task dialog) — then
 * commits the DB write and rolls back on failure. Mirrors {@link useUpdateTask},
 * scoped to a subtask instead of a task. On success it invalidates the
 * tasks query. The server syncs calendar columns. On failure it restores the
 * pre-mutation snapshot, captures the error to Sentry, surfaces a toast, and
 * returns `false`.
 *
 * This is the query-cache-aware replacement for the pre-migration update.
 */
export function useUpdateSubtask() {
  const queryClient = useQueryClient();

  return async (
    subtaskId: string,
    updates: SubtaskUpdates,
  ): Promise<boolean> => {
    // Snapshot before mutating so a failed write can be rolled back, and so the
    // calendar sync can merge onto the pre-update subtask, as it did before.
    const previousTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks);

    // Optimistic update — reflected instantly in every consumer of useTasks.
    // Left alone when the cache has not loaded: writing `[]` into it would flip
    // useTasks out of its pending state and drop the app's loading gate, and
    // the rollback's snapshot guard could not put it back.
    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      prev
        ? prev.map((t) => ({
            ...t,
            subtasks: t.subtasks?.map((st) =>
              st.id === subtaskId ? { ...st, ...updates } : st,
            ),
          }))
        : prev,
    );

    const revision = new Date().toISOString();
    const { error } = await supabase
      .from("subtasks")
      .update({ ...updates, updated_at: revision })
      .eq("id", subtaskId);

    if (error) {
      // Restore the whole snapshot if the update fails.
      if (previousTasks) {
        queryClient.setQueryData(queryKeys.tasks, previousTasks);
      }
      Sentry.captureException(error, {
        extra: { mutation: "useUpdateSubtask", subtaskId },
      });
      toast.error("Failed to update subtask. Please try again.");
      return false;
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
